import { useState } from 'react';
import moment from 'moment';
import { IconCalendar as Calendar } from '@tabler/icons-react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { deleteEvent, getMyDrafts } from '../apiEndpoints';
import Trash from '@/icons/Trash';
import Eye from '@/icons/Eye';
import Plus from '@/icons/Plus';
import { Spinner } from '@heroui/spinner';
import DeleteConfirmModal from './DeleteConfirmModal';
import { SearchBox } from './admin/shared';
import PayoutManagement from './PayoutManagement';
import { useAuth } from '../contexts/auth-context';
import { useUserRoles, getUserRoleForOrganizer } from '../utils/roleUtils';
import { useOrganization } from '../contexts/organization-context';
import { currencySymbol } from '@/utils/currency';
import { chip, chipActive, chipIdle, btnAccent, EmptyState } from '@/components/ui/design';

// Define an Event type
interface EventItem {
    _id?: string;
    currency?: string;
    id?: string;
    image: string;
    title: string;
    date: string;
    totalSold: number;
    totalCapacity: number;
    totalRevenue: number;
    viewUrl?: string;
    isPast: boolean;
    times: Array<{
        startDate: string;
        endDate: string;
        startTime?: string;
        endTime?: string;
    }>;
    attachments?: {
        banner?: {
            link: string;
        };
    };
    eventType?: string;
    authorizedEmails?: string[];
}

// Accept event prop
export default function EventsComponent({ events, setViewEventIdx, isEventsLoading, isEventsError, navigate }: {
    events?: EventItem[],
    setViewEventIdx?: (idx: number) => void,
    isEventsLoading?: boolean,
    isEventsError?: boolean,
    navigate?: (path: string) => void,
}) {
    console.log(events);
    const { user } = useAuth();
    const { data: userRolesData } = useUserRoles();
    const { selectedOrganizerId } = useOrganization();
    const ownedOrganizerIds = userRolesData?.ownedOrganizerIds ?? [];
    const isOwner = !!selectedOrganizerId && ownedOrganizerIds.includes(selectedOrganizerId);
    const roleForSelectedOrg = selectedOrganizerId
        ? getUserRoleForOrganizer(userRolesData?.roles ?? [], selectedOrganizerId)
        : null;
    const hideRevenue = !isOwner && roleForSelectedOrg === 'usher';
    const [eventsTab, setEventsTab] = useState<'all' | 'upcoming' | 'past' | 'drafts' | 'payouts'>('all');
    const [welcomeDismissed, setWelcomeDismissed] = useState(false);
    const [search, setSearch] = useState('');

    // Fetch drafts
    const {
        data: draftsData,
        isFetching: isDraftsLoading,
        isError: isDraftsError,
    } = useQuery({
        queryKey: ['userDrafts', user?.userId],
        queryFn: async () => {
            if (!user?.userId) return { data: [] };
            return await getMyDrafts();
        },
        enabled: !!user?.userId,
    });

    const allDrafts = draftsData?.data || [];
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [eventToDelete, setEventToDelete] = useState<{ id: string; title: string } | null>(null);
    const [deletingEventId, setDeletingEventId] = useState<string | null>(null);

    const queryClient = useQueryClient();

    // Delete event mutation
    const deleteEventMutation = useMutation({
        mutationFn: (eventId: string) => deleteEvent(eventId),
        onSuccess: () => {
            // Invalidate events query to refresh the list
            queryClient.invalidateQueries({ queryKey: ['userEvents'] });
            // Also invalidate drafts query if we're on drafts tab
            if (eventsTab === 'drafts') {
                queryClient.invalidateQueries({ queryKey: ['userDrafts', user?.userId] });
            }
            setDeleteModalOpen(false);
            setEventToDelete(null);
            setDeletingEventId(null);
        },
        onError: (error: any) => {
            console.error('Failed to delete event:', error);
            setDeletingEventId(null);
            // You can add toast notification here if needed
        }
    });

    const handleDeleteClick = (e: React.MouseEvent, event: EventItem) => {
        e.stopPropagation();
        const eventId = (event as any)?._id || (event as any)?.id;
        if (eventId) {
            setEventToDelete({ id: eventId, title: event.title });
            setDeleteModalOpen(true);
        }
    };

    const handleConfirmDelete = () => {
        if (eventToDelete) {
            setDeletingEventId(eventToDelete.id);
            deleteEventMutation.mutate(eventToDelete.id);
        }
    };

    const handleCancelDelete = () => {
        setDeleteModalOpen(false);
        setEventToDelete(null);
        setDeletingEventId(null);
    };

    // Search matches the title, venue or address of events and drafts
    const query = search.trim().toLowerCase();
    const matchesSearch = (item: any) => !query || [item?.title, item?.venue, item?.address?.fullAddress, item?.address?.city].some((value) => String(value ?? '').toLowerCase().includes(query));
    const drafts = allDrafts.filter(matchesSearch);
    const matchingEvents = (events || []).filter(matchesSearch);
    const upcomingEvents = matchingEvents.filter((event: EventItem) => moment(event.times?.[0]?.startDate).isAfter(moment()));
    const pastEvents = matchingEvents.filter((event: EventItem) => moment(event.times?.[0]?.startDate).isBefore(moment()));
    const upcomingCount = upcomingEvents.length;
    const pastCount = pastEvents.length;
    const draftsCount = drafts.length;

    const eventTabs: { key: 'all' | 'upcoming' | 'drafts' | 'past'; label: string; count: number }[] = [
        { key: 'all', label: 'All', count: upcomingCount + pastCount + draftsCount },
        { key: 'upcoming', label: 'On-Sale', count: upcomingCount },
        { key: 'drafts', label: 'Draft', count: draftsCount },
        { key: 'past', label: 'Past', count: pastCount },
    ];

    // Keep nudging the user toward creating their first event until one actually exists (published or draft).
    // Unfiltered, so a search with no matches doesn't bring back the "create your first event" welcome
    const totalEventsCount = (events || []).length + allDrafts.length;
    const isLoadingEventCounts = !!isEventsLoading || isDraftsLoading;
    const showWelcome = !welcomeDismissed && !isLoadingEventCounts && totalEventsCount === 0;

    const formatDateRow = (startDate: string, endDate: string) => {
        const start = moment(startDate);
        const end = moment(endDate);
        if (start.isSame(end, 'day')) {
            return (
                <>
                    <span className="font-semibold text-grey10 text-sm dark:text-white">{start.format('ddd D MMM')}</span>
                    <span className="text-grey40 text-sm font-normal dark:text-nightMuted"> from </span>
                    <span className="font-semibold text-sm text-grey10 dark:text-white">{start.format('h:mm A')} - {end.format('h:mm A')}</span>
                </>
            );
        }
        return (
            <span className="font-semibold text-sm text-grey10 dark:text-white">{start.format('MMM D, YYYY')} - {end.format('MMM D, YYYY')}</span>
        );
    };

    const renderSection = (label: string, node: React.ReactNode) => (
        <div className="mb-12 last:mb-0" key={label}>
            <div className="text-[clamp(28px,4vw,40px)] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white mb-5">{label}</div>
            {node}
        </div>
    );

    const renderEventCard = (event: EventItem) => {
        const eventId = (event as any)?._id || (event as any)?.id;
        const isDeleting = deletingEventId === eventId;
        const goToEvent = () => {
            if (setViewEventIdx) {
                const idx = (events || []).findIndex((e: EventItem) => ((e as any)?._id || (e as any)?.id) === eventId);
                if (idx >= 0) setViewEventIdx(idx);
            }
            if (eventId && navigate) navigate(`/organizations/events/${eventId}`);
        };
        const showRevenue = event.eventType === 'created' && !hideRevenue;
        const showSold = event.eventType === 'created';
        return (
            <div
                key={eventId}
                className="group rounded-[28px] bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06] p-3 sm:p-4 hover:bg-grey95 dark:hover:bg-white/[0.08] transition-colors cursor-pointer flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-3 sm:gap-6"
                onClick={goToEvent}
            >
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 flex-1 min-w-0 sm:min-w-[220px]">
                    <img src={event.attachments?.banner?.link} alt={event.title} className="w-full h-44 sm:w-[185px] sm:h-[104px] rounded-[20px] object-cover flex-shrink-0 bg-grey95 dark:bg-white/[0.06]" />
                    <div className="min-w-0 flex-1 w-full">
                        <div className="font-bold tracking-[-0.02em] text-lg sm:text-xl text-grey10 dark:text-white line-clamp-2 sm:truncate">{event.title}</div>
                        <div className="flex items-center gap-1.5 text-sm mt-2">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                                <path d="M13.334 2.5V5.83333M6.66732 2.5V5.83333M3.33398 9.16667H16.6673M3.33398 5.83333C3.33398 5.39131 3.50958 4.96738 3.82214 4.65482C4.1347 4.34226 4.55862 4.16667 5.00065 4.16667H15.0007C15.4427 4.16667 15.8666 4.34226 16.1792 4.65482C16.4917 4.96738 16.6673 5.39131 16.6673 5.83333V15.8333C16.6673 16.2754 16.4917 16.6993 16.1792 17.0118C15.8666 17.3244 15.4427 17.5 15.0007 17.5H5.00065C4.55862 17.5 4.1347 17.3244 3.82214 17.0118C3.50958 16.6993 3.33398 16.2754 3.33398 15.8333V5.83333ZM6.66732 12.5H8.33398V14.1667H6.66732V12.5Z" stroke="#A8A8A8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>

                            <span className="truncate">{formatDateRow(event.times[0].startDate, event.times[0].endDate)}</span>
                        </div>
                    </div>
                </div>

                {(showRevenue || showSold) && (
                    <div className="flex items-center justify-start sm:justify-end gap-6 sm:gap-8 px-1 pb-1 sm:p-0 sm:pr-4">
                        {showRevenue && (
                            <div className="flex flex-col items-start sm:items-center gap-0.5 flex-shrink-0 sm:min-w-[80px]">
                                <span className="text-lg font-bold tracking-[-0.02em] text-grey10 dark:text-white whitespace-nowrap">{currencySymbol(event.currency)}{event.totalRevenue.toFixed(0)}</span>
                                <span className="text-xs text-grey40 dark:text-nightMuted">{currencySymbol(event.currency)}0 Today</span>
                            </div>
                        )}

                        {showSold && (
                            <div className="flex flex-col items-start sm:items-center gap-0.5 flex-shrink-0 sm:min-w-[80px]">
                                <span className="text-lg font-bold tracking-[-0.02em] text-grey10 dark:text-white whitespace-nowrap">{event.totalSold} / {event.totalCapacity}</span>
                                <span className="text-xs text-grey40 dark:text-nightMuted">0 Today</span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderDraftCard = (draft: any) => {
        const draftId = draft._id || draft.id;
        const isDeleting = deletingEventId === draftId;
        const draftStart = draft.times?.[0]?.startDate;
        const draftEnd = draft.times?.[0]?.endDate;
        return (
            <div
                key={draftId}
                className="group rounded-[28px] bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06] p-3 sm:p-4 hover:bg-grey95 dark:hover:bg-white/[0.08] transition-colors cursor-pointer flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6"
                onClick={() => { if (draftId && navigate) navigate(`/organizations/events/create?eventId=${draftId}`); }}
            >
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 flex-1 min-w-0 sm:min-w-[220px]">
                    <img
                        src={draft.attachments?.banner?.link || '/placeholder-event.png'}
                        alt={draft.title || 'Draft Event'}
                        className="w-full h-44 sm:w-[185px] sm:h-[104px] rounded-[20px] object-cover flex-shrink-0 bg-grey95 dark:bg-white/[0.06]"
                    />
                    <div className="min-w-0 flex-1 w-full">
                        <div className="font-bold tracking-[-0.02em] text-lg sm:text-xl text-grey10 dark:text-white line-clamp-2 sm:truncate">
                            {draft.title || 'Untitled Event'}
                        </div>
                        <div className="flex items-center gap-1.5 text-sm mt-2">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                                <path d="M13.334 2.5V5.83333M6.66732 2.5V5.83333M3.33398 9.16667H16.6673M3.33398 5.83333C3.33398 5.39131 3.50958 4.96738 3.82214 4.65482C4.1347 4.34226 4.55862 4.16667 5.00065 4.16667H15.0007C15.4427 4.16667 15.8666 4.34226 16.1792 4.65482C16.4917 4.96738 16.6673 5.39131 16.6673 5.83333V15.8333C16.6673 16.2754 16.4917 16.6993 16.1792 17.0118C15.8666 17.3244 15.4427 17.5 15.0007 17.5H5.00065C4.55862 17.5 4.1347 17.3244 3.82214 17.0118C3.50958 16.6993 3.33398 16.2754 3.33398 15.8333V5.83333ZM6.66732 12.5H8.33398V14.1667H6.66732V12.5Z" stroke="#A8A8A8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>

                            <span className="truncate">{formatDateRow(draft?.times?.[0]?.startDate, draft?.times?.[0]?.endDate)}</span>
                        </div>
                        {/* {draftStart && draftEnd ? (
                            <div className="flex items-center gap-1.5 text-sm mt-1.5">
                                <Calendar className="w-4 h-4 text-[#888] dark:text-nightTextSecondary/50 flex-shrink-0" />
                                <span className="truncate">{formatDateRow(draftStart, draftEnd)}</span>
                            </div>
                        ) : (
                            <div className="text-xs text-[#888] dark:text-nightMuted mt-1.5">
                                Step {((draft.currentStep || 0) + 1)} of 4
                            </div>
                        )} */}
                    </div>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0 pt-1 sm:pt-0 sm:ml-auto">
                    {/*  <button
                        className="flex-1 sm:flex-none px-4 py-2 bg-primary40 dark:bg-nightAccent text-white dark:text-[#080808] rounded text-sm font-semibold hover:bg-primary50 transition-colors"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (draftId && navigate) navigate(`/organizations/events/create?eventId=${draftId}`);
                        }}
                    >
                        Continue
                    </button> */}
                    {/*  <button
                        data-testid="delete-draft-button"
                        className="w-9 h-9 rounded border border-gray-200 dark:border-nightBorder flex items-center justify-center hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-50 flex-shrink-0"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (draftId) {
                                setEventToDelete({ id: draftId, title: draft.title || 'Untitled Event' });
                                setDeleteModalOpen(true);
                            }
                        }}
                        disabled={isDeleting}
                    >
                        {isDeleting ? <Spinner color="danger" size="sm" /> : <Trash width={18} height={18} />}
                    </button> */}
                </div>
            </div>
        );
    };

    return (
        <div className="w-full">
            {showWelcome && (
                <div className="mb-8 flex items-start gap-3 rounded-[28px] bg-primary99 ring-1 ring-primary/10 dark:bg-white/[0.06] dark:ring-white/10 px-5 sm:px-7 py-5">
                    <div className="flex-1">
                        <div className="text-base font-bold tracking-[-0.01em] text-primary20 dark:text-nightAccent">Welcome! Your organization is ready.</div>
                        <div className="text-sm text-grey40 dark:text-nightMuted mt-1">You can now create events, sell tickets, and manage your organization from here.</div>
                    </div>
                    <button
                        type="button"
                        onClick={() => setWelcomeDismissed(true)}
                        aria-label="Dismiss"
                        className="flex h-8 w-8 items-center justify-center rounded-full text-primary20 hover:bg-white dark:text-nightAccent dark:hover:bg-white/10 dark:hover:text-white flex-shrink-0 transition-colors"
                    >
                        <svg width="16" height="16" fill="none" viewBox="0 0 16 16"><path d="M4 4l8 8M4 12L12 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                    </button>
                </div>
            )}
            <div className="mb-4">
                <SearchBox value={search} onChange={setSearch} placeholder="Search events by title or venue" />
            </div>
            {/* Events Tabs */}
            <div className="flex flex-col-reverse sm:flex-row items-center justify-between mb-10 gap-4">
                <div className="-mx-4 flex gap-2 items-center w-[calc(100%+2rem)] overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:w-full sm:flex-wrap sm:overflow-visible sm:px-0">
                    {eventTabs.map(tab => {
                        const active = eventsTab === tab.key;
                        return (
                            <button
                                key={tab.key}
                                className={`${chip} flex items-center gap-2 pl-4 pr-2 ${active ? chipActive : chipIdle}`}
                                onClick={() => setEventsTab(tab.key)}
                            >
                                {tab.label}
                                <span className={`min-w-[24px] h-6 px-2 rounded-full text-xs font-bold flex items-center justify-center ${active ? 'bg-white/20 text-white dark:bg-nightBg/15 dark:text-nightBg' : 'bg-white text-grey20 ring-1 ring-black/[0.06] dark:bg-white/10 dark:text-nightTextSecondary dark:ring-0'}`}>
                                    {tab.count}
                                </span>
                            </button>
                        );
                    })}
                </div>
                <button
                    className={`${btnAccent} w-full sm:w-fit flex-shrink-0 whitespace-nowrap`}
                    onClick={() => navigate?.('/organizations/events/create')}
                >
                    <Plus width={14} height={14} className='text-white dark:text-nightBg' />
                    Create Event
                </button>
            </div>
            {eventsTab === 'payouts' ? (
                <PayoutManagement />
            ) : (
                <>
                    {(eventsTab === 'all' || eventsTab === 'upcoming') && (
                        isEventsLoading ? renderSection('On-Sale', <div className="text-center py-12 text-grey40 dark:text-nightMuted">Loading events...</div>) :
                            isEventsError ? renderSection('On-Sale', <div className="rounded-[28px] bg-red-50 text-center py-12 text-red-500 dark:bg-red-500/10 dark:text-red-400">Failed to load events.</div>) :
                                upcomingEvents.length === 0 ? (
                                    eventsTab === 'upcoming' ? renderSection('On-Sale', <EmptyState title="No events found." />) : null
                                ) : renderSection('On-Sale', <div className="flex flex-col gap-3">{upcomingEvents.map((event: EventItem) => renderEventCard(event))}</div>)
                    )}

                    {(eventsTab === 'all' || eventsTab === 'drafts') && (
                        isDraftsLoading ? renderSection('Draft', <div className="text-center py-12 text-grey40 dark:text-nightMuted">Loading drafts...</div>) :
                            isDraftsError ? renderSection('Draft', <div className="rounded-[28px] bg-red-50 text-center py-12 text-red-500 dark:bg-red-500/10 dark:text-red-400">Failed to load drafts.</div>) :
                                !drafts || drafts.length === 0 ? (
                                    eventsTab === 'drafts' ? renderSection('Draft', <EmptyState title="No drafts found." />) : null
                                ) : renderSection('Draft', <div className="flex flex-col gap-3">{drafts.map((draft: any) => renderDraftCard(draft))}</div>)
                    )}

                    {(eventsTab === 'all' || eventsTab === 'past') && (
                        isEventsLoading ? renderSection('Past', <div className="text-center py-12 text-grey40 dark:text-nightMuted">Loading events...</div>) :
                            isEventsError ? renderSection('Past', <div className="rounded-[28px] bg-red-50 text-center py-12 text-red-500 dark:bg-red-500/10 dark:text-red-400">Failed to load events.</div>) :
                                pastEvents.length === 0 ? (
                                    eventsTab === 'past' ? renderSection('Past', <EmptyState title="No events found." />) : null
                                ) : renderSection('Past', <div className="flex flex-col gap-3">{pastEvents.map((event: EventItem) => renderEventCard(event))}</div>)
                    )}

                    {eventsTab === 'all' && !isEventsLoading && !isDraftsLoading && !isEventsError && !isDraftsError &&
                        upcomingEvents.length === 0 && pastEvents.length === 0 && (!drafts || drafts.length === 0) && (
                            <EmptyState title={query ? 'No events match your search.' : 'No events found.'} />
                        )}
                </>
            )}

            {/* Delete Confirmation Modal */}
            <DeleteConfirmModal
                isOpen={deleteModalOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title={eventsTab === 'drafts' ? 'Delete Draft' : 'Delete Event'}
                message={`Are you sure you want to delete "${eventToDelete?.title}"? This action cannot be undone.`}
                loading={deletingEventId !== null}
            />
        </div>
    );
} 